"use client"

import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

import {
  membersMockData,
  currentUserId,
  currentUserRole,
} from "@/features/members/model/members-mock-data"
import { useTenantSettingsStore } from "@/features/settings/services/general/general-store"
import {
  buildMemberFullName,
  EmploymentType,
  type Member,
  type MemberAdminUpdateInput,
  type MemberInviteRecord,
  MemberInviteStatus,
  type MemberOnboardingInput,
  type MemberProfileUpdateInput,
  type MemberRole,
  MemberStatus,
} from "@/features/members/model/members-types"

function createId(prefix: string) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`
}

function splitMemberName(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length <= 1) {
    return { firstName: parts[0] ?? "", middleName: "", lastName: "" }
  }
  if (parts.length === 2) {
    return { firstName: parts[0], middleName: "", lastName: parts[1] }
  }
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(" "),
    lastName: parts[parts.length - 1],
  }
}

function mergeMemberPatch(member: Member, patch: Partial<Member>) {
  const next = { ...member, ...patch }
  const seed = splitMemberName(member.name)
  const firstName = next.firstName ?? seed.firstName
  const middleName = next.middleName ?? seed.middleName
  const lastName = next.lastName ?? seed.lastName
  return {
    ...next,
    firstName,
    middleName,
    lastName,
    name: buildMemberFullName({
      firstName,
      middleName,
      lastName,
      fallback: next.name,
    }),
  }
}

function buildInvite(memberId: string, email: string, actor: string): MemberInviteRecord {
  const sentAt = new Date().toISOString()
  const inviteExpiryDays =
    useTenantSettingsStore.getState().getSettings().people.inviteExpiryDays
  const expiresAt = new Date(
    Date.now() + 1000 * 60 * 60 * 24 * Math.max(inviteExpiryDays, 1)
  ).toISOString()
  return {
    id: createId("inv"),
    memberId,
    email,
    status: MemberInviteStatus.Sent,
    sentAt,
    sentBy: actor,
    tokenRef: createId("token"),
    expiresAt,
  }
}

type MembersState = {
  members: Member[]
  invites: MemberInviteRecord[]
  currentUserId: string
  currentUserRole: MemberRole
  replaceMembers: (members: Member[]) => void
  upsertMember: (member: Member) => void
  listMembers: () => Member[]
  listInvites: () => MemberInviteRecord[]
  getMemberById: (id: string) => Member | undefined
  updateMember: (id: string, patch: Partial<Member>) => void
  updateMemberProfile: (id: string, patch: MemberProfileUpdateInput) => void
  updateMemberAdmin: (id: string, patch: Partial<MemberAdminUpdateInput>) => void
  createMemberFromOnboarding: (input: MemberOnboardingInput) => Member
  createInvite: (memberId: string, actor: string) => MemberInviteRecord
  updateMembers: (ids: string[], patch: Partial<Member>) => void
  deleteMember: (id: string) => void
}

export const useMembersStore = create<MembersState>()(
  persist(
    (set, get) => ({
  members: membersMockData,
  invites: [],
  currentUserId,
  currentUserRole,
  replaceMembers: (members) => set({ members }),
  upsertMember: (member) => {
    set((state) => {
      const exists = state.members.some((entry) => entry.id === member.id)
      if (exists) {
        return {
          members: state.members.map((entry) => (entry.id === member.id ? member : entry)),
        }
      }

      return {
        members: [member, ...state.members],
      }
    })
  },
  listMembers: () => get().members,
  listInvites: () => get().invites,
  getMemberById: (id) => get().members.find((member) => member.id === id),
  updateMember: (id, patch) => {
    set((state) => ({
      members: state.members.map((member) =>
        member.id === id ? mergeMemberPatch(member, patch) : member
      ),
    }))
  },
  updateMemberProfile: (id, patch) => {
    set((state) => ({
      members: state.members.map((member) =>
        member.id === id ? mergeMemberPatch(member, patch) : member
      ),
    }))
  },
  updateMemberAdmin: (id, patch) => {
    set((state) => ({
      members: state.members.map((member) =>
        member.id === id ? mergeMemberPatch(member, patch) : member
      ),
    }))
  },
  createMemberFromOnboarding: (input) => {
    const now = new Date().toISOString()
    const fullName = buildMemberFullName({
      firstName: input.firstName,
      middleName: input.middleName,
      lastName: input.lastName,
    })
    const emailSeed = `${input.firstName ?? ""}.${input.lastName ?? ""}`
      .toLowerCase()
      .replace(/[^a-z0-9.]/g, "")
      .replace(/^\.+|\.+$/g, "")
    const generatedEmail = `${emailSeed || "member"}.${Math.random().toString(36).slice(2, 6)}@local.church`
    const derivedPlaceOfBirth = [input.placeOfBirthCountry, input.placeOfBirthCity].filter(Boolean).join(", ")

    const next: Member = {
      id: createId("mem"),
      name: fullName,
      firstName: input.firstName,
      middleName: input.middleName,
      lastName: input.lastName,
      email: input.email?.trim() || generatedEmail,
      phone: input.phone,
      occupation: input.occupation ?? null,
      address: input.address,
      notes: input.notes,
      gender: input.gender,
      dateOfBirth: input.dateOfBirth,
      placeOfBirth: derivedPlaceOfBirth || input.placeOfBirth,
      placeOfBirthCountry: input.placeOfBirthCountry,
      placeOfBirthCity: input.placeOfBirthCity,
      maritalStatus: input.maritalStatus,
      marriageType: input.marriageType,
      baptized: input.baptized,
      baptizedAt: input.baptizedAt,
      baptismPlace: input.baptismPlace,
      fatherFirstName: input.fatherFirstName,
      fatherMiddleName: input.fatherMiddleName,
      fatherLastName: input.fatherLastName,
      motherFirstName: input.motherFirstName,
      motherMiddleName: input.motherMiddleName,
      motherLastName: input.motherLastName,
      spouseFirstName: input.spouseFirstName,
      spouseMiddleName: input.spouseMiddleName,
      spouseLastName: input.spouseLastName,
      guardianFirstName: input.guardianFirstName,
      guardianMiddleName: input.guardianMiddleName,
      guardianLastName: input.guardianLastName,
      branchId: input.branchId,
      groupIds: input.groupIds,
      ministryIds: input.ministryIds,
      role: "viewer",
      team: "Community",
      status: input.status ?? MemberStatus.Active,
      employmentType: EmploymentType.FullTime,
      lastActiveAt: now,
      joinedAt: now,
    }
    set((state) => ({
      members: [next, ...state.members],
    }))
    return next
  },
  createInvite: (memberId, actor) => {
    const member = get().members.find((entry) => entry.id === memberId)
    if (!member) {
      throw new Error("Member not found.")
    }
    if (!member.email) {
      throw new Error("Member email is required to create an invite.")
    }

    const invite = buildInvite(member.id, member.email, actor)
    set((state) => ({
      invites: [invite, ...state.invites],
    }))
    return invite
  },
  updateMembers: (ids, patch) => {
    const selected = new Set(ids)
    set((state) => ({
      members: state.members.map((member) =>
        selected.has(member.id) ? mergeMemberPatch(member, patch) : member
      ),
    }))
  },
  deleteMember: (id) => {
    set((state) => ({
      members: state.members.filter((member) => member.id !== id),
      invites: state.invites.filter((invite) => invite.memberId !== id),
    }))
  },
    }),
    {
      name: "members-store-v1",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        members: state.members,
        invites: state.invites,
        currentUserId: state.currentUserId,
        currentUserRole: state.currentUserRole,
      }),
    }
  )
)
